// Shell.tsx — react-bootstrap page shell shared across openfactory SPAs.
import React, { useState, useEffect, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Navbar from 'react-bootstrap/Navbar';
import Nav from 'react-bootstrap/Nav';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Stack from 'react-bootstrap/Stack';
import { BsBoxArrowRight, BsSunFill, BsMoonFill } from 'react-icons/bs';
import { getTheme, setTheme as setCookieTheme } from './utils/theme';

export interface ShellNavItem {
    label: string;
    href: string;
}

export interface ShellProps {
    brand: ReactNode;
    brandHref?: string;
    navItems: ShellNavItem[];
    /**
     * Product-specific chrome pinned to the right of the nav links, ahead of
     * the account avatar — a status readout the app wants visible on every
     * page rather than only on the page that owns it (imprinter's streak, for
     * instance). Omitted by every app that has nothing to put there, which
     * leaves the navbar exactly as it was.
     */
    headerExtra?: ReactNode;
    userEmail?: string;
    userPhotoUrl?: string;
    userHref?: string;
    onLogout?: () => void;
    children: ReactNode;
}

export const Shell: React.FC<ShellProps> = ({
    brand,
    brandHref = '/',
    navItems,
    headerExtra,
    userEmail,
    userPhotoUrl,
    userHref,
    onLogout,
    children,
}) => {
    const location = useLocation();
    const [theme, setTheme] = useState<string>(() =>
        typeof document === 'undefined' ? 'darkly' : getTheme(),
    );

    useEffect(() => {
        const link = document.getElementById('theme-link') as HTMLLinkElement | null;
        if (link) {
            link.href = `https://cdn.jsdelivr.net/npm/bootswatch@5.3.3/dist/${theme}/bootstrap.min.css`;
        }
        document.documentElement.dataset.bsTheme = theme === 'darkly' ? 'dark' : 'light';
    }, [theme]);

    const toggleTheme = () => {
        const next = theme === 'darkly' ? 'flatly' : 'darkly';
        setTheme(next);
        setCookieTheme(next);
    };

    return (
        <>
            <Navbar variant="dark" expand="lg" sticky="top" style={{ backgroundColor: '#303633' }}>
                <Container fluid>
                    <Navbar.Brand as={Link} to={brandHref} className="d-flex align-items-center gap-2">
                        {brand}
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="of-shell-navbar" />
                    <Navbar.Collapse id="of-shell-navbar">
                        <Nav className="me-auto">
                            {navItems.map((it) => (
                                <Nav.Link
                                    key={it.href}
                                    as={Link}
                                    to={it.href}
                                    active={location.pathname === it.href}
                                >
                                    {it.label}
                                </Nav.Link>
                            ))}
                        </Nav>
                        <Nav className="align-items-center gap-2">
                            {headerExtra && (
                                <span className="d-flex align-items-center me-2">{headerExtra}</span>
                            )}
                            {userEmail && (() => {
                                const avatar = userPhotoUrl ? (
                                    <img
                                        src={userPhotoUrl}
                                        alt={userEmail}
                                        title={userEmail}
                                        referrerPolicy="no-referrer"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            objectFit: 'cover',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            flexShrink: 0,
                                        }}
                                    />
                                ) : (
                                    <span
                                        title={userEmail}
                                        aria-label={userEmail}
                                        className="d-inline-flex align-items-center justify-content-center text-light"
                                        style={{
                                            width: 32,
                                            height: 32,
                                            borderRadius: '50%',
                                            background: 'rgba(255,255,255,0.1)',
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            fontSize: '0.9rem',
                                            fontWeight: 600,
                                            textTransform: 'uppercase',
                                            flexShrink: 0,
                                        }}
                                    >
                                        {userEmail.charAt(0)}
                                    </span>
                                );
                                return userHref ? (
                                    <Link to={userHref} className="d-flex align-items-center me-2">{avatar}</Link>
                                ) : (
                                    <span className="d-flex align-items-center me-2">{avatar}</span>
                                );
                            })()}
                            <Stack
                                direction="horizontal"
                                className="align-items-center ms-2 border-start ps-3"
                                style={{ borderColor: 'rgba(255,255,255,0.2)' }}
                            >
                                <BsSunFill size={18} className="text-warning" />
                                <Form.Check
                                    type="switch"
                                    id="of-shell-theme-switch"
                                    label=""
                                    checked={theme === 'darkly'}
                                    onChange={toggleTheme}
                                    className="mx-2"
                                />
                                <BsMoonFill size={18} className="text-light" />
                            </Stack>
                            {userEmail && onLogout && (
                                <Button
                                    variant="outline-light"
                                    onClick={onLogout}
                                    className="d-flex align-items-center gap-2"
                                >
                                    <BsBoxArrowRight size={18} /> Logout
                                </Button>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            {/* Full-bleed: the page gets the whole panel and caps only its own
                prose (DESIGN.md C-LAYOUT-1). `fluid` still supplies the
                container's horizontal padding, so this is not text against the
                glass — it is a page that grows with the display instead of
                stopping at Bootstrap's 1320px step. */}
            <Container fluid className="py-4">{children}</Container>
        </>
    );
};

export default Shell;
